import { useCallback, useRef } from 'react';

import { resolveRowScrollTop } from './resolveRowScrollTop';

/** 固定ヘッダを見つけるための目印。付けた要素のぶんだけ行を下げる */
export const STICKY_HEADER_ATTR = 'data-sticky-header';

/** 縦スクロールする最寄りの祖先。見つからなければ null */
function findScrollParent(element: HTMLElement): HTMLElement | null {
  let node = element.parentElement;
  while (node) {
    const { overflowY } = window.getComputedStyle(node);
    if ((overflowY === 'auto' || overflowY === 'scroll') && node.scrollHeight > node.clientHeight) {
      return node;
    }
    node = node.parentElement;
  }
  return null;
}

/**
 * 車両行の DOM を rowId で覚えておき、あとからスクロールで見せる。
 * 旧地図（V3Plans/Presenter.tsx:716-727）の deliveryElementsRef と同じ仕組み。
 *
 * 地図のマーカーから車両を選んだとき、左リストがその行まで動かないと
 * 「選んだのに何も起きていない」ように見える。
 */
export function useScrollToRow() {
  const elementsRef = useRef<Map<string, HTMLElement>>(new Map());

  /** null で登録を消す。アンマウント済みの DOM を掴み続けないため */
  const registerRow = useCallback((rowId: string, element: HTMLElement | null) => {
    if (element) elementsRef.current.set(rowId, element);
    else elementsRef.current.delete(rowId);
  }, []);

  const scrollToRow = useCallback((rowId: string) => {
    // 絞り込みで消えた行を選ぶことがある。登録が無ければ何もしない
    const element = elementsRef.current.get(rowId);
    if (!element) return;

    const container = findScrollParent(element);
    // スクロールしない一覧（全部見えている）では動かす必要が無い
    if (!container) return;

    const header = container.querySelector<HTMLElement>(`[${STICKY_HEADER_ATTR}]`);
    container.scrollTop = resolveRowScrollTop({
      scrollTop: container.scrollTop,
      containerTop: container.getBoundingClientRect().top,
      rowTop: element.getBoundingClientRect().top,
      headerHeight: header?.offsetHeight ?? 0,
      maxScrollTop: container.scrollHeight - container.clientHeight,
    });
  }, []);

  return { registerRow, scrollToRow };
}
